import { createColumnHelper } from "@tanstack/react-table";
import { GetAllExpeditionResponseDtoExpedition } from "@/types/api/api-types";
import { Button } from "@/components/ui/button";
import TrashOutlineIcon from "@/assets/icons/trash-outline.svg?react";
import EditOutlineIcon from "@/assets/icons/edit-outline.svg?react";
import { getFile } from "@/api/s3.api";
import { ActionData } from "@/types/tableAction";
import { CountryBadge } from "../shared/badge/country.badge";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import { PendingShipmentsTableElem, PendingShipmentsText } from "./pendingShipmentsText";

const columnHelper = createColumnHelper<GetAllExpeditionResponseDtoExpedition>();

type Props = {
  setActionData: (actionData: ActionData) => void;
  navigate: (path: string) => void;
};

export const pendingCustomQuotationTableColumns = ({ setActionData, navigate }: Props) => {
  const handlePdfClick = async (orderKey: string) => {
    if (!orderKey) return;
    const url = await getFile(orderKey);
    window.open(url, "_blank");
  };

  return [
    columnHelper.display({
      id: "identifier",
      header: () => (
        <PendingShipmentsText
          render={(t) => t("pendingShipmentsCustomQuotationColumns.identifier")}
        />
      ),
      cell: (info) => (
        <div className="max-w-[300px] truncate text-sm">
          {info.row.original.quotation
            ? "C-" + info.row.original.quotation?.identifier
            : "D-" + info.row.original.customQuotation?.identifier}
        </div>
      ),
    }),
    columnHelper.display({
      id: "createdAt",
      header: () => (
        <PendingShipmentsText
          render={(t) => t("pendingShipmentsCustomQuotationColumns.createdAt")}
        />
      ),
      cell: (info) => (
        <PendingShipmentsTableElem
          name={(t) => t("pendingShipmentsCustomQuotationColumns.requestDateLabel")}
        >
          <div className="flex w-fit flex-col items-center gap-0">
            <p className="text-sm text-gray-900">
              {new Date(info.row.original.createdAt).toLocaleDateString("fr-FR")}
            </p>
            <p className="text-sm text-gray-600">
              <PendingShipmentsText
                render={(t) =>
                  t("pendingShipmentsCustomQuotationColumns.createdAtTime", {
                    time: new Date(info.row.original.createdAt).toLocaleTimeString("fr-FR", {
                      hour: "2-digit",
                      minute: "2-digit",
                    }),
                  })
                }
              />
            </p>
          </div>
        </PendingShipmentsTableElem>
      ),
    }),
    columnHelper.display({
      id: "client",
      header: () => (
        <PendingShipmentsText render={(t) => t("pendingShipmentsCustomQuotationColumns.client")} />
      ),
      cell: (info) => {
        const client =
          info.row.original.deliveryInfo.company ||
          (info.row.original.deliveryInfo.firstName && info.row.original.deliveryInfo.lastName
            ? info.row.original.deliveryInfo.firstName +
              " " +
              info.row.original.deliveryInfo.lastName
            : "-");
        return (
          <PendingShipmentsTableElem
            name={(t) => t("pendingShipmentsCustomQuotationColumns.clientLabel")}
          >
            <div className="max-w-[300px] truncate text-sm font-bold">{client}</div>
          </PendingShipmentsTableElem>
        );
      },
    }),
    columnHelper.display({
      id: "country",
      header: () => (
        <PendingShipmentsText render={(t) => t("pendingShipmentsCustomQuotationColumns.country")} />
      ),
      cell: (info) => (
        <PendingShipmentsTableElem
          name={(t) => t("pendingShipmentsCustomQuotationColumns.countryLabel")}
        >
          <CountryBadge country={info.row.original.deliveryInfo?.country || "-"} className="" />
        </PendingShipmentsTableElem>
      ),
    }),
    columnHelper.display({
      id: "createdBy",
      header: () => (
        <PendingShipmentsText
          render={(t) => t("pendingShipmentsCustomQuotationColumns.createdBy")}
        />
      ),
      cell: (info) => (
        <PendingShipmentsTableElem
          name={(t) => t("pendingShipmentsCustomQuotationColumns.createdByLabel")}
        >
          <div className="max-w-[300px] truncate text-sm">
            {info.row.original.createdBy
              ? info.row.original.createdBy.firstName + " " + info.row.original.createdBy.lastName
              : "-"}
          </div>
        </PendingShipmentsTableElem>
      ),
    }),
    columnHelper.display({
      id: "orderKey",
      header: () => (
        <PendingShipmentsText render={(t) => t("pendingShipmentsCustomQuotationColumns.quote")} />
      ),
      cell: (info) => {
        const row = info.row.original;
        const quote = row.expeditionDocuments?.documents?.find(
          (document) => document.type === FileTypeEnum.EXPEDITION_QUOTE,
        );
        if (quote) {
          return (
            <PendingShipmentsTableElem
              name={(t) => t("pendingShipmentsCustomQuotationColumns.quoteLabel")}
            >
              <button
                onClick={() => handlePdfClick(quote.path)}
                className="flex flex-row items-center gap-2 focus:outline-hidden"
              >
                <a>{quote.name}</a>
              </button>
            </PendingShipmentsTableElem>
          );
        }
        return (
          <PendingShipmentsTableElem
            name={(t) => t("pendingShipmentsCustomQuotationColumns.quoteLabel")}
          >
            <button
              onClick={() => {}}
              className="flex flex-row items-center gap-2 focus:outline-hidden"
              disabled
            >
              <a>-</a>
            </button>
          </PendingShipmentsTableElem>
        );
      },
    }),
    columnHelper.display({
      id: "actionOne",
      header: "",
      cell: (info) => (
        <div className="flex flex-row items-center justify-center gap-3">
          <TrashOutlineIcon
            className="cursor-pointer text-slate-600 hover:text-slate-800"
            onClick={() => {
              setActionData({
                expedition: info.row.original,
                action: "DELETE",
              });
            }}
          />
          <EditOutlineIcon
            className="cursor-pointer text-slate-600 hover:text-slate-800"
            onClick={() => {
              navigate(`/custom-cotations/${info.row.original.id}`);
            }}
          />
        </div>
      ),
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      cell: (info) => {
        return (
          <Button
            variant="ghost"
            size="icon"
            className="w-fit px-2 text-sm text-blue-700 hover:bg-transparent hover:text-blue-800"
            onClick={() => {
              setActionData({
                expedition: info.row.original,
                action: "FINALIZE",
              });
            }}
          >
            <PendingShipmentsText
              render={(t) => t("pendingShipmentsCustomQuotationColumns.finalize")}
            />
          </Button>
        );
      },
    }),
  ];
};
